import { environment, Environment } from '@toni/util'

import serverError from './static-pages/server-error'
import serverForbidden from './static-pages/server-forbidden'

interface FetchPageContext {
  url: string,
  brand: string,
  sweepstakesActive?: boolean,
  token?: string
}

interface PageData {
  header: any,
  body: any,
  pageMeta: any,
  metaData: any,
  footer?: any
}

interface FetchPageResponse {
  pageData: PageData,
  brand: string,
  imageBrand: string,
  status: number
}

interface RedirectError extends Error {
  data: {
    status: number,
    redirectUrl: string
  }
}

const isProd = environment === Environment.production

const getNoneContentRoutes = (): Record<string, Record<string, string>> => {
  // eventuell auch eine andere datenbeschaffung möglich -> longrun
  return {}
}

// Ada autonative static data
const adaAutonative = {
  article: {
    header: { menu: [] },
    body: {
      header: {
        roofline: 'Artikel-Roofline',
        headline: 'Artikel-Headline',
        introductionText: '<p>Introduction-Text</p>',
        image: {
          imageCredit: 'Bildnachweis',
          width: 1600,
          height: 900,
          layout: {
            size: 'xl'
          },
          imageDescription: {
            plain: 'Bildunterschrift'
          }
        }
      },
      paragraphs: [
        {
          paragraphType: 'text',
          text: '<p>Artikeltext</p>'
        },
        {
          paragraphType: 'horizontalLine',
          horizontalLine: {
            lineTitle: 'MEHR ZUM THEMA'
          }
        },
        {
          paragraphType: 'teaserGroup',
          teaserGroup: {
            teaserGroupType: 'bx-teaser-container--one-fourth-width',
            teasers: [
              {
                imageId: '83f993e0',
                titleLong: 'Example in article Teaser-Headline',
                advertorial: true,
                roofline: 'Example in article Teaser-Roofline',
                url: '#'
              }
            ]
          }
        },
        {
          paragraphType: 'adaAutoNative'
        }
      ]
    },
    footer: {},
    pageMeta: {
      pageType: 'article',
      url: ''
    },
    metaData: {
      adaAutoNative: true,
      advertorial: true,
      noIndex: true
    }
  },
  structureterm: {
    header: { menu: [] },
    body: {
      paragraphs: [
        {
          paragraphType: 'teaserGroup',
          teaserGroup: {
            teaserGroupType: 'bx-teaser-container--one-fourth-width',
            teasers: [
              {
                imageId: '83f993e0',
                titleLong: 'Example Teaser-Headline',
                advertorial: true,
                roofline: 'Example Teaser-Roofline',
                url: '#'
              }
            ]
          }
        },
        {
          paragraphType: 'adaAutoNative'
        }
      ]
    },
    footer: {},
    pageMeta: {
      pageType: 'structureterm',
      url: ''
    },
    metaData: {
      adaAutoNative: true,
      advertorial: true,
      noIndex: true
    }
  },
  pagestrip: {
    header: { menu: [] },
    body: { paragraphs: [] },
    footer: {},
    pageMeta: { url: '' },
    metaData: {}
  }
}

export async function fetchPage (context: FetchPageContext, proxyServiceURL?: string): Promise<FetchPageResponse> {
  const { url, brand, sweepstakesActive } = context
  const adaNativeRegex = /^\/(autonative|pagestrip)(\/artikel\.html)?(\?.*)?$/
  const adaNativeMatch = url.match(adaNativeRegex)
  const adaNativeRequest = adaNativeMatch ? adaNativeMatch[1] : false
  const encodedUrlParameter = encodeURIComponent(url)
  // This is not really an env variable. It will get replaced by webpack with what's in the configs under composerServiceUrl
  const composerUrl = proxyServiceURL ?? `${process.env.COMPOSER_URL}`
  let constructedUrl = `${composerUrl}/page/${brand}?url=${encodedUrlParameter}&fields=metaData.teaser:0`

  // workaround until whitelabel gets branded and provides data on gui, cs, etc
  if (brand === 'whitelabel' || (!isProd && (brand === 'autozeitung' || brand === 'tvmovie'))) {
    constructedUrl = `${composerUrl}/page/maennersache?url=${encodedUrlParameter}&fields=metaData.teaser:0`
  }

  // lets fetch pageData for Home on adaAutonative request, to get navigation structure
  if (adaNativeRequest) {
    constructedUrl = `${composerUrl}/page/${brand}?url=%2F&fields=metaData.teaser:0`
  }

  if (sweepstakesActive && url.startsWith('/gewinnspiele')) {
    const firstPart = url.split('?')[0]
    if (firstPart && firstPart.replace(/\/\s*$/, '').split('/').length > 3) {
      // sweepstakes do not support folder structures. only 1 depth. (ignore trailing slash with the replace while checking)
      // else will result in 503, so we just fetch the 404 page from composer to save the composer throwing a 503
      console.info('Attempted to fetch invalid sweepstake, sending 404: ', url)
      constructedUrl = `${composerUrl}/page/${brand}?url=%2F404`
    } else {
      const sweepstakesUrl = new URL(composerUrl + url)
      sweepstakesUrl.pathname = sweepstakesUrl.pathname.replace('/gewinnspiele', '/sweepstakes')
      sweepstakesUrl.search = new URLSearchParams({
        brand,
        ...(sweepstakesUrl.searchParams.has('page') && { page: sweepstakesUrl.searchParams.get('page')! })
      }).toString()
      constructedUrl = sweepstakesUrl.href
    }
  }

  // if url has a brand parameter we pass this to the frontend only (not the composer) - this helps with checking content with styles of other brands
  const frontendBrand = isProd ? brand : (url.split('brand=').splice(1).join('').split('&')[0] || brand)

  const noneContentRoutes = getNoneContentRoutes()
  if (noneContentRoutes[brand] && noneContentRoutes[brand][url]) {
    return {
      pageData: {
        header: {},
        body: {
          headline: '',
          paragraphs: [
            {
              paragraphType: 'vueComponent',
              vueComponent: {
                componentName: noneContentRoutes[brand][url]
              }
            }
          ]
        },
        pageMeta: {},
        metaData: {}
      },
      brand: frontendBrand,
      imageBrand: brand,
      status: 200
    }
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (context.token) {
      headers.authorization = context.token
    }

    const response = await fetch(constructedUrl, {
      method: 'GET',
      headers
    })

    const asset = await response.json()

    // Handle redirects (check this first, regardless of response status)
    if (asset && asset.customInfo && asset.customInfo.redirectCode === 301 && asset.customInfo.redirectUrl) {
      const redirectError = new Error('Redirect required') as RedirectError
      const status = asset.customInfo.redirectCode

      // append all parameter to redirect url excluding page=0 and page=1
      const urlParams = new URLSearchParams(url.indexOf('?') >= 0 ? url.slice(url.indexOf('?')) : '')
      if (['0', '1'].includes(urlParams.get('page') || '')) {
        urlParams.delete('page')
      }
      const query = urlParams.toString() !== '' ? `?${urlParams.toString()}` : ''

      const redirectUrl = `${asset.customInfo.redirectUrl}${query}`
      redirectError.data = { status, redirectUrl }
      throw redirectError
    }

    // Handle HTTP error responses
    if (!response.ok) {
      if (response.status === 403) {
        return {
          status: 403,
          pageData: serverForbidden,
          brand,
          imageBrand: brand
        }
      }

      // Handle 404
      if (response.status === 404 || (asset && asset.status === 404)) {
        return {
          status: 404,
          pageData: (asset && asset.customInfo) ? asset.customInfo : serverError,
          brand,
          imageBrand: brand
        }
      }

      console.error(`ERROR FROM COMPOSER FETCHING URL: ${constructedUrl} - Status: ${response.status}`)
    }

    // catch AdA native page request (autonative and pagestrip) and enrich static data
    if (adaNativeRequest && adaNativeRequest !== 'pagestrip') {
      const adaNativeData = adaNativeRequest === 'autonative'
        ? url.includes('.html') ? adaAutonative.article : adaAutonative.structureterm
        : adaAutonative.pagestrip
      if (asset?.header?.menu) {
        adaNativeData.header.menu = asset.header.menu
      }
      if (asset?.footer) {
        adaNativeData.footer = asset.footer
      }
      adaNativeData.pageMeta.url = url
      return {
        pageData: adaNativeData,
        brand: frontendBrand,
        imageBrand: brand,
        status: 200
      }
    }

    return {
      pageData: asset,
      brand: frontendBrand,
      imageBrand: brand,
      status: 200
    }
  } catch (err: any) {
    // Check if this is a redirect error that should be rethrown
    if (err.data && err.data.status && err.data.redirectUrl) {
      throw err
    }

    // Handle network or other errors
    console.error('ERROR : 503 in api/index.ts', err)
    return {
      status: 503,
      pageData: serverError,
      brand,
      imageBrand: brand
    }
  }
}
